/**
 * 图片和视频URL优化工具
 * 用于处理后台数据传过来的图片和视频URL
 */

/**
 * 优化图片URL
 * @param {string} url - 原始图片URL
 * @param {object} options - 优化选项
 * @param {number} options.width - 目标宽度
 * @param {number} options.height - 目标高度
 * @param {number} options.quality - 图片质量 (1-100)
 * @param {string} options.format - 图片格式 (webp, jpeg, png)
 * @returns {string} 优化后的URL
 */
export function optimizeImageUrl(url, options = {}) {
  if (!url || typeof url !== 'string') {
    return url;
  }

  // 如果是base64或data URI，直接返回
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  // 如果是本地资源（require导入的），直接返回
  if (typeof url === 'object' || url.includes('static/media')) {
    return url;
  }

  const {
    width,
    height,
    quality = 85,
    format = 'webp'
  } = options;

  try {
    const urlObj = new URL(url, window.location.origin);
    
    // 如果URL已经包含优化参数，不重复添加
    if (urlObj.searchParams.has('w') || urlObj.searchParams.has('q')) {
      return url;
    }

    // 添加优化参数
    if (width) urlObj.searchParams.set('w', width);
    if (height) urlObj.searchParams.set('h', height);
    urlObj.searchParams.set('q', quality);
    
    // 如果支持WebP，尝试使用WebP格式
    if (format === 'webp' && supportsWebP()) {
      urlObj.searchParams.set('format', 'webp');
    }

    return urlObj.toString();
  } catch (e) {
    // URL解析失败，返回原始URL
    return url;
  }
}

/**
 * 优化视频URL
 * @param {string} url - 原始视频URL
 * @param {object} options - 优化选项
 * @param {string} options.preload - 预加载策略 (none, metadata, auto)
 * @returns {string} 优化后的URL
 */
export function optimizeVideoUrl(url, options = {}) {
  if (!url || typeof url !== 'string') {
    return url;
  }

  // 如果是base64或data URI，直接返回
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  // 视频URL通常不需要修改，但可以添加CDN参数等
  return url;
}

/**
 * 检查浏览器是否支持WebP
 * @returns {boolean}
 */
function supportsWebP() {
  if (typeof window === 'undefined') return false;
  
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
}

/**
 * 递归处理对象中的图片和视频URL
 * @param {any} data - 要处理的数据（可以是对象、数组或基本类型）
 * @param {object} options - 优化选项
 * @returns {any} 处理后的数据
 */
export function optimizeMediaUrls(data, options = {}) {
  if (!data) return data;

  // 数组处理
  if (Array.isArray(data)) {
    return data.map(item => optimizeMediaUrls(item, options));
  }

  // 对象处理
  if (typeof data === 'object' && data !== null) {
    const optimized = {};
    
    for (const [key, value] of Object.entries(data)) {
      // 识别图片URL字段
      if (isImageUrlField(key, value)) {
        optimized[key] = optimizeImageUrl(value, {
          width: options.imageWidth,
          height: options.imageHeight,
          quality: options.imageQuality,
          format: options.imageFormat
        });
      }
      // 识别视频URL字段
      else if (isVideoUrlField(key, value)) {
        optimized[key] = optimizeVideoUrl(value, options);
      }
      // 递归处理嵌套对象
      else if (typeof value === 'object' && value !== null) {
        optimized[key] = optimizeMediaUrls(value, options);
      }
      else {
        optimized[key] = value;
      }
    }
    
    return optimized;
  }

  return data;
}

/**
 * 判断是否是图片URL字段
 * @param {string} key - 字段名
 * @param {any} value - 字段值
 * @returns {boolean}
 */
function isImageUrlField(key, value) {
  if (typeof value !== 'string') return false;
  
  const imageKeys = [
    'imgUrl', 'imageUrl', 'iconUrl', 'cover', 'image', 
    'avatar', 'logo', 'thumbnail', 'img', 'photo', 'picture',
    'ImgCover', 'imgCover', 'icon', 'banner'
  ];
  
  return imageKeys.some(k => key.toLowerCase().includes(k.toLowerCase()));
}

/**
 * 判断是否是视频URL字段
 * @param {string} key - 字段名
 * @param {any} value - 字段值
 * @returns {boolean}
 */
function isVideoUrlField(key, value) {
  if (typeof value !== 'string') return false;
  
  const videoKeys = ['videoUrl', 'video', 'mp4', 'mov', 'webm'];
  
  return videoKeys.some(k => key.toLowerCase().includes(k.toLowerCase()));
}

/**
 * 预加载图片
 * @param {string} url - 图片URL
 * @returns {Promise<HTMLImageElement>}
 */
export function preloadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * 预加载视频
 * @param {string} url - 视频URL
 * @returns {Promise<HTMLVideoElement>}
 */
export function preloadVideo(url) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => resolve(video);
    video.onerror = reject;
    video.src = url;
  });
}

